import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Removes the inner padding — use when the body is a full-bleed grid. */
  flush?: boolean;
}

/** The one container shape the whole application uses. */
export function Card({ flush, className, children, ...rest }: CardProps) {
  return (
    <section className={cn('bb-card overflow-hidden', !flush && 'bb-pad', className)} {...rest}>
      {children}
    </section>
  );
}

export interface CardHeaderProps {
  /**
   * A line *above* the title, in tabular figures — an account code, a document
   * number. The identifier is what somebody scanned the list for and the name
   * is what confirms they found it, so the code reads first and the name gets
   * the weight. Distinct from `subtitle`, which explains rather than identifies.
   */
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function CardHeader({ eyebrow, title, subtitle, actions, className }: CardHeaderProps) {
  return (
    <div className={cn('bb-card-head flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        {eyebrow && (
          <span className="bb-mono block text-[12px] leading-tight text-[var(--ink-3)]">
            {eyebrow}
          </span>
        )}
        <h2
          className={cn(
            'm-0 leading-tight font-semibold text-[var(--ink)]',
            eyebrow ? 'mt-[3px] text-[15px] tracking-[-0.015em] [text-wrap:pretty]' : 'text-[14px]',
          )}
        >
          {title}
        </h2>
        {subtitle && (
          <span className="mt-1 block text-[11.5px] text-[var(--ink-3)]">{subtitle}</span>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('bb-pad', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-t border-[var(--line-2)] bg-[var(--surface-2)] px-[var(--pad)] py-3',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * The action bar for voucher screens: Save draft / Post / Print.
 *
 * `placement` is the whole of the difference. `bottom` is the original sticky
 * strip, pinned to the foot of the viewport so Post stays reachable part-way
 * down a forty-line grid. `top` is a plain bar that sits *above* the cards and
 * scrolls with them — chosen for the voucher screen because a bar pinned over
 * the bottom of the page permanently covered the last grid row and the card
 * footer under it, and the entry form already states what it is about to post
 * in the panel beside the grid. It is a card in its own right rather than page
 * chrome, so it carries the same border and radius as the ones below it.
 */
export function ActionBar({
  children,
  className,
  placement = 'bottom',
}: {
  children: ReactNode;
  className?: string;
  placement?: 'top' | 'bottom';
}) {
  return (
    <div
      data-noprint="1"
      className={cn(
        'flex items-center justify-end gap-2 px-[var(--pad)] py-3',
        placement === 'bottom'
          ? 'sticky bottom-0 z-10 border-t border-[var(--line)] bg-[var(--surface)]/95 backdrop-blur'
          : 'bb-card mb-3.5 border-[var(--line)] bg-[var(--surface)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Horizontal filter/search strip above a grid. */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      data-noprint="1"
      className={cn(
        'flex flex-wrap items-center gap-2 border-b border-[var(--line-2)] px-[var(--pad)] py-2.5',
        className,
      )}
    >
      {children}
    </div>
  );
}
